import {FC, useContext, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import 'dayjs/locale/ru';

import Toolbar from '@mui/material/Toolbar/Toolbar';
import Typography from '@mui/material/Typography/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import ControlledInput from '../../../../../../ui/controlled-input/ControlledInput';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhoneInput from '../../../../../../ui/phone-input/PhoneInput';
import IconButton from '@mui/material/IconButton';

import {IContactPersonFormInputs} from '../../../../../../shared/types/person.types';

import {emailRegExp} from '../../../../../../shared/validation/emailRegExp';

import {PersonService} from '../../../../../../api/services/person.service';
import {PartyRelationsService} from '../../../../../../api/services/partyRelations.service';

import {currentCompanyIdState} from '../../../../../../store/company/atoms';
import {contactPersonsOfCompanyQueryTrigger} from '../../../../../../store/contact-person/atoms';
import {Tooltip} from '@mui/material';
import {EventBusContext} from '../../../../../../providers/EventBus';

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'scale(0.85) translate(-59%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 2,
};

interface IAddContactPersonOfCompany {
  open: boolean;
  handleClose: () => void;
}

export const AddContactPersonOfCompany: FC<IAddContactPersonOfCompany> = ({open, handleClose}) => {
  const {getValues, control, reset, trigger} = useForm<IContactPersonFormInputs>({
    defaultValues: {
      birthDate: null,
    },
    mode: 'onChange',
  });
  const currentCompanyId = useRecoilValue(currentCompanyIdState);
  const triggerContactPersons = useSetRecoilState(contactPersonsOfCompanyQueryTrigger);
  const [dateError, setDateError] = useState<any>();
  const [loading, setLoading] = useState(false);
  const {bus} = useContext(EventBusContext);

  const onSubmit = async () => {
    try {
      const data = getValues();
      setLoading(true);
      const {data: createdPerson} = await PersonService.create({
        firstName: data.firstName,
        lastName: data.lastName,
        patronymic: data.patronymic || null,
        phoneNumber: data.phoneNumber,
        phoneNumber2: null,
        email: data.email,
        birthDate: data.birthDate?.format('YYYY-MM-DD') || null,
      });

      await PartyRelationsService.create({
        relationTitle: data.relationTitle,
        description: data.description,
        relationType: 'CONTACT_PERSON',
        fromId: createdPerson.id,
        toId: currentCompanyId,
      });

      setTimeout(() => {
        setLoading(false);
        triggerContactPersons((v) => v + 1);
        handleClose();
        reset();
      }, 1500);
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        !loading && handleClose();
      }}
    >
      <Box sx={style}>
        <Toolbar
          sx={{
            pl: {sm: 1},
            pr: {xs: 1, sm: 1},
          }}
        >
          <Typography sx={{flex: '1 1 100%'}} variant="h6" id="formTitle" component="div">
            Добавление контактного лица
          </Typography>
        </Toolbar>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" spacing={2}>
            <ControlledInput
              control={control}
              name="lastName"
              rules={{
                required: 'Обязательно для заполнения',
              }}
              size="small"
              label="Фамилия"
            />
            <ControlledInput
              control={control}
              name="firstName"
              rules={{
                required: 'Обязательно для заполнения',
              }}
              size="small"
              label="Имя"
            />
            <ControlledInput control={control} name="patronymic" size="small" label="Отчество" />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Stack direction="row">
              <PhoneInput
                control={control}
                name="phoneNumber"
                rules={{
                  required: 'Обязательно для заполнения',
                  pattern: {
                    value: /^(\+7 \([1-9]\d{2}\) \d{3}-\d{2}-\d{2}( #\d+)?$)/,
                    message: 'Некорректный номер или добавочный',
                  },
                }}
                size="small"
                label="Номер телефона"
              />
              <Tooltip
                title={
                  <>
                    <Typography variant="subtitle1">
                      Формат номера:<em>{' +7 (ххх) ххх-хх-хх'}</em>
                    </Typography>
                    <Typography variant="subtitle1">
                      Формат возможного добавочного:<em>{' #хххх'}</em>
                    </Typography>
                    <Typography variant="subtitle1">
                      Итого:<em>{' +7 (ххх) ххх-хх-хх  #хххх'}</em>
                    </Typography>
                  </>
                }
              >
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <ControlledInput
              control={control}
              name="email"
              rules={{
                required: 'Обязательно для заполнения',
                pattern: {
                  value: emailRegExp,
                  message: 'Некорректный email',
                },
              }}
              size="small"
              label="Email"
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <ControlledInput
              control={control}
              name="relationTitle"
              rules={{
                required: 'Обязательно для заполнения',
                maxLength: {
                  value: 255,
                  message: 'Максимум 255 символов',
                },
              }}
              size="small"
              label="Должность"
            />
            <Controller
              control={control}
              name="birthDate"
              render={({field: {value, onChange}}) => (
                <LocalizationProvider adapterLocale="ru" dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Дата рождения"
                    format="YYYY.MM.DD"
                    value={value}
                    onError={(reason) => setDateError(reason)}
                    disableFuture
                    onChange={onChange}
                    // renderInput={(params) => (
                    //     <TextField
                    //         {...params}
                    //         size="small"
                    //         inputProps={{
                    //             ...params.inputProps,
                    //             placeholder: 'гггг.мм.дд'
                    //         }}
                    //     />
                    // )}
                  />
                </LocalizationProvider>
              )}
            />
          </Stack>
          <ControlledInput
            control={control}
            name="description"
            rules={{
              required: 'Обязательно для заполнения',
              maxLength: {
                value: 400,
                message: 'Максимум 400 символов',
              },
            }}
            size="small"
            multiline
            label="Зона ответственности"
            additionalFunc={() => {
              trigger('description');
            }}
          />
          <Stack direction="row" sx={{justifyContent: 'center'}}>
            <LoadingButton
              type="submit"
              variant="contained"
              disabled={!!dateError}
              loading={loading}
              onClick={() => {
                trigger().then((bool) => {
                  bool && onSubmit();
                });
              }}
            >
              Сохранить
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};
